/**
 * useClientCustomMetrics — save/load reusable custom metrics (Story 16.3).
 *
 * Mirrors the useClientMarkups/goals hook pattern: a tenant/client-scoped
 * useQuery list + useMutation save/delete that invalidate the list on success.
 * Backs the report editor's CustomMetricsCategoryPanel "Reusable metrics"
 * section. The server route (`/api/clients/:id/custom-metrics`) persists into
 * the Story 16.1 `custom_metrics` table; the list returns this client's saved
 * metrics PLUS tenant-global ones.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiDelete } from "../lib/apiClient";

export interface CustomMetricVariableShape {
  id: string;
  label: string;
  metricKey: string;
  /** Story 16.4 — per-variable platform binding for cross-platform unions. */
  platform?: string;
}

export interface SavedCustomMetric {
  id: string;
  name: string;
  formulaExpression: string;
  sourceMetrics: CustomMetricVariableShape[];
  outputFormat: "number" | "currency" | "percentage";
  clientId: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface SaveCustomMetricPayload {
  name: string;
  formulaExpression: string;
  sourceMetrics: CustomMetricVariableShape[];
  outputFormat: "number" | "currency" | "percentage";
  /** Story 16.4 — union members recorded in metric_mappings (optional). */
  metricMappings?: Array<{ integrationType: string; fieldName: string }>;
}

function customMetricsKey(clientId: string | undefined) {
  return ["client-custom-metrics", clientId] as const;
}

/** List the reusable custom metrics available for a client (saved + tenant-global). */
export function useClientCustomMetrics(clientId: string | undefined) {
  return useQuery<SavedCustomMetric[]>({
    queryKey: customMetricsKey(clientId),
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${clientId}/custom-metrics`);
      if (!res.ok) return [];
      const json = (await res.json()) as { data: { metrics: SavedCustomMetric[] } };
      return json.data.metrics;
    },
    enabled: !!clientId,
  });
}

async function readError(res: Response, fallback: string): Promise<string> {
  const body = (await res.json().catch(() => null)) as
    | { detail?: string; title?: string }
    | null;
  return body?.detail ?? body?.title ?? `${fallback} (${res.status})`;
}

/** Save the current formula draft as a named, reusable, client-scoped metric. */
export function useSaveCustomMetric(clientId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: SaveCustomMetricPayload
    ): Promise<SavedCustomMetric> => {
      const res = await apiPost(
        `/api/clients/${clientId}/custom-metrics`,
        payload
      );
      if (!res.ok) throw new Error(await readError(res, "Failed to save metric"));
      const json = (await res.json()) as { data: { metric: SavedCustomMetric } };
      return json.data.metric;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customMetricsKey(clientId) });
    },
  });
}

/** Delete a saved reusable custom metric. */
export function useDeleteCustomMetric(clientId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (metricId: string): Promise<void> => {
      const res = await apiDelete(
        `/api/clients/${clientId}/custom-metrics/${metricId}`
      );
      if (!res.ok) throw new Error(await readError(res, "Failed to delete metric"));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customMetricsKey(clientId) });
    },
  });
}
